import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';

export const DEFAULT_PPM_RATE_TABLE: PPMRate[] = [
  { balanceLessThan: 5.99, minimumPayment: 100, calculationType: 'actualAmount' },
  { balanceLessThan: 50, minimumPayment: 5.99, calculationType: 'fixed' },
  { balanceLessThan: 75, minimumPayment: 7.99, calculationType: 'fixed' },
  { balanceLessThan: 100, minimumPayment: 9.99, calculationType: 'fixed' },
  { balanceLessThan: 150, minimumPayment: 14.99, calculationType: 'fixed' },
  { balanceLessThan: 200, minimumPayment: 19.99, calculationType: 'fixed' },
  { balanceLessThan: 250, minimumPayment: 24.99, calculationType: 'fixed' },
  { balanceLessThan: 300, minimumPayment: 27.99, calculationType: 'fixed' },
  { balanceLessThan: 350, minimumPayment: 28.99, calculationType: 'fixed' },
  { balanceLessThan: 450, minimumPayment: 29.99, calculationType: 'fixed' },
  { balanceLessThan: 600, minimumPayment: 33.99, calculationType: 'fixed' },
  { balanceLessThan: 800, minimumPayment: 46.99, calculationType: 'fixed' },
  { balanceLessThan: 1100, minimumPayment: 59.99, calculationType: 'fixed' },
  { balanceLessThan: 1300, minimumPayment: 69.99, calculationType: 'fixed' },
  { balanceLessThan: 999999, minimumPayment: 5, calculationType: 'percentage' },
];

export type PPMRate = {
  balanceLessThan: number;
  minimumPayment: number;
  calculationType?: 'fixed' | 'actualAmount' | 'percentage';
};

export type PPMScenario = 'anonymousOrZeroBalance' | 'existingIncrease' | 'existingNoIncrease' | 'fallback';

export type CustomerPPMType = 'anonymous' | 'nonCredit' | 'zeroBalance' | 'existingBalance';

export type PPMPage = 'cart' | 'checkout' | 'orderConfirmation';

export type PPMData = {
  scenario: PPMScenario;
  currentPPM: number;
  newPPM: number;
  increase: number;
  hasIncrease?: boolean;
  accountBalance?: number;
  combinedAmount?: number;
};

export type FlattenedCustomer = {
  accountBalance?: number;
  monthlyPaymentAmt?: number;
  creditAccountId?: string;
  availableCredit?: number;
};

export const ppmRateTable = (projectSettings: ProjectSettings): PPMRate[] => {
  try {
    const parsed = JSON.parse(projectSettings?.ppmRateTable ?? '[]');

    return parsed?.length ? parsed : DEFAULT_PPM_RATE_TABLE;
  } catch {
    return DEFAULT_PPM_RATE_TABLE;
  }
};

/**
 * customer helpers
 */

export const getAccountBalance = (customer?: FlattenedCustomer) => {
  return customer?.accountBalance ?? 0;
};

export const getCurrentMonthlyPayment = (customer?: FlattenedCustomer) => {
  return customer?.monthlyPaymentAmt ?? 0;
};

export const hasCreditAccount = (customer?: FlattenedCustomer) => {
  return Boolean(customer?.creditAccountId);
};

export const hasExistingCreditBalance = (customer?: FlattenedCustomer) => {
  return getAccountBalance(customer) > 0;
};

/**
 * classify customer
 */

export const getCustomerPPMType = ({
  isLoggedIn,
  customer,
}: {
  isLoggedIn: boolean;
  customer?: FlattenedCustomer;
}): CustomerPPMType => {
  if (!isLoggedIn) {
    return 'anonymous';
  }

  if (!hasCreditAccount(customer)) {
    return 'nonCredit';
  }

  if (getAccountBalance(customer) <= 0) {
    return 'zeroBalance';
  }

  return 'existingBalance';
};

/**
 * generic PPM calculation
 *
 * Used for:
 *
 * anonymous
 * no credit account
 * zero balance
 *
 * AND
 *
 * existing customer
 * scenario A
 */

export const calculatePPM = (amount: number, projectSettings: ProjectSettings): number => {
  const rateTable = ppmRateTable(projectSettings);

  const matchedRate = rateTable.find((rate) => amount < rate.balanceLessThan);

  if (!matchedRate) {
    return 0;
  }

  switch (matchedRate.calculationType) {
    case 'actualAmount':
      return Number(amount.toFixed(2));

    case 'percentage':
      return Number(((amount * matchedRate.minimumPayment) / 100).toFixed(2));

    case 'fixed':
    default:
      return matchedRate.minimumPayment;
  }
};

/**
 * existing customer
 *
 * FINAL credit logic:
 *
 * Use accountBalance + current order total
 * to calculate the new PPM.
 *
 * Compare newPPM with monthlyPaymentAmt
 * to determine increase/no increase.
 */

export const getExistingCustomerPPMDetails = ({
  customer,
  orderTotal,
  projectSettings,
}: {
  customer?: FlattenedCustomer;
  orderTotal: number;
  projectSettings: ProjectSettings;
}) => {
  const accountBalance = getAccountBalance(customer);
  const currentPPM = getCurrentMonthlyPayment(customer);

  const combinedAmount = Number((accountBalance + orderTotal).toFixed(2));

  const calculatedPPM = calculatePPM(combinedAmount, projectSettings);

  const newPPM = Math.max(calculatedPPM, currentPPM);

  const hasIncrease = newPPM > currentPPM;

  return {
    currentPPM,
    accountBalance,
    combinedAmount,
    newPPM,
    hasIncrease,
    increase: Number((newPPM - currentPPM).toFixed(2)),
  };
};

/**
 * master resolver
 */

export const getPPMData = ({
  customerType,
  customer,
  orderTotal,
  projectSettings,
}: {
  customerType: CustomerPPMType;
  customer?: FlattenedCustomer;
  orderTotal: number;
  projectSettings: ProjectSettings;
}): PPMData | null => {
  switch (customerType) {
    case 'anonymous':
    case 'nonCredit':
    case 'zeroBalance':
      return {
        scenario: 'anonymousOrZeroBalance',
        currentPPM: 0,
        newPPM: calculatePPM(orderTotal, projectSettings),
        increase: 0,
      };

    case 'existingBalance': {
      const details = getExistingCustomerPPMDetails({
        customer,
        orderTotal,
        projectSettings,
      });

      return {
        scenario: details.hasIncrease ? 'existingIncrease' : 'existingNoIncrease',
        ...details,
      };
    }

    default:
      return null;
  }
};
